// frontend/src/components/widgets/MultiZoneConfig.js
import React, { useState, useEffect } from 'react';
import { Layout, X, Save, Grid, Plus, Trash2, Image, List, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MultiZoneConfig = ({ existingConfig = null, onSave, onCancel }) => {
  const { t } = useTranslation();
  
  // State
  const [config, setConfig] = useState({
    name: existingConfig?.name || '',
    config: {
      layoutType: existingConfig?.config?.layoutType || '2-horizontal',
      zones: existingConfig?.config?.zones || [],
      backgroundColor: existingConfig?.config?.backgroundColor || '#000000',
      gapSize: existingConfig?.config?.gapSize || 4
    }
  });

  const [playlists, setPlaylists] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [media, setMedia] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);

  // Layout Templates - definiert wie viele Zonen jedes Layout hat
  const LAYOUT_TEMPLATES = {
    '2-horizontal': { zones: 2, grid: 'grid-cols-2 grid-rows-1' },
    '2-vertical': { zones: 2, grid: 'grid-cols-1 grid-rows-2' },
    '3-vertical': { zones: 3, grid: 'grid-cols-1 grid-rows-3' },
    '3-horizontal': { zones: 3, grid: 'grid-cols-3 grid-rows-1' },
    '3-main-side': { zones: 3, grid: 'grid-cols-3 grid-rows-2' },
    '4-grid': { zones: 4, grid: 'grid-cols-2 grid-rows-2' },
    '4-main-corners': { zones: 4, grid: 'grid-cols-3 grid-rows-3' },
    '2-news-ticker': { zones: 2, grid: 'grid-cols-1 grid-rows-[85fr_15fr]' },
    '4-sidebar-ticker': { zones: 4, grid: 'grid-cols-[1fr_4fr] grid-rows-[7fr_1.5fr_1.5fr]' }
  };

  // Load verfügbare Inhalte (Playlists, Widgets, Media)
  useEffect(() => {
    loadAvailableContent();
  }, []);

  // Update Zones wenn Layout-Typ sich ändert
  useEffect(() => {
    const template = LAYOUT_TEMPLATES[config.config.layoutType];
    if (template && config.config.zones.length !== template.zones) {
      const newZones = Array.from({ length: template.zones }, (_, i) => {
        // Behalte existierende Zone-Config wenn vorhanden
        return config.config.zones[i] || {
          zoneId: `zone-${i + 1}`,
          contentType: 'playlist',
          contentId: '',
          duration: 30
        };
      });
      
      setConfig(prev => ({
        ...prev,
        config: {
          ...prev.config,
          zones: newZones
        }
      }));
    }
  }, [config.config.layoutType]);

  const loadAvailableContent = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

      // Load Playlists
      const playlistsRes = await fetch(`${API_URL}/playlists`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const playlistsData = await playlistsRes.json();
      setPlaylists(playlistsData || []);

      // Load Widgets (alle aktivierten Features)
      const featuresRes = await fetch(`${API_URL}/features`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const featuresData = await featuresRes.json();
      
      // Lade Instanzen für alle aktivierten Features
      const allWidgets = [];
      for (const feature of featuresData.features || []) {
        if (feature.isActivated) {
          const instancesRes = await fetch(`${API_URL}/features/${feature.id}/instances`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const instancesData = await instancesRes.json();
          allWidgets.push(...(instancesData.instances || []));
        }
      }
      setWidgets(allWidgets);

      // Load Media
      const mediaRes = await fetch(`${API_URL}/media`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const mediaData = await mediaRes.json();
      setMedia(mediaData || []);

      setLoading(false);
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
    }
  };

  const handleNameChange = (value) => {
    setConfig(prev => ({ ...prev, name: value }));
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: null }));
    }
  };

  const handleLayoutTypeChange = (value) => {
    setConfig(prev => ({
      ...prev,
      config: { ...prev.config, layoutType: value }
    }));
  };

  const handleZoneChange = (zoneIndex, field, value) => {
    setConfig(prev => ({
      ...prev,
      config: {
        ...prev.config,
        zones: prev.config.zones.map((zone, idx) =>
          idx === zoneIndex ? { ...zone, [field]: value } : zone
        )
      }
    }));

    // Clear zone-specific error
    if (errors[`zone-${zoneIndex}`]) {
      setErrors(prev => ({ ...prev, [`zone-${zoneIndex}`]: null }));
    }
  };

  const handleBackgroundColorChange = (value) => {
    setConfig(prev => ({
      ...prev,
      config: { ...prev.config, backgroundColor: value }
    }));
  };

  const handleGapSizeChange = (value) => {
    setConfig(prev => ({
      ...prev,
      config: { ...prev.config, gapSize: parseInt(value) || 0 }
    }));
  };

  const validateForm = () => {
  console.log('🔍 validateForm called!');
  console.log('📋 Validating config:', config);
  
  const newErrors = {};

  if (!config.name.trim()) {
    newErrors.name = t('widgets.multiZone.errors.nameRequired');
  }

  if (!config.config.layoutType) {
    newErrors.layoutType = t('widgets.multiZone.errors.layoutTypeRequired');
  }

  // Validiere jede Zone
  config.config.zones.forEach((zone, idx) => {
    if (!zone.contentId) {
      newErrors[`zone-${idx}`] = t('widgets.multiZone.errors.contentRequired', { number: idx + 1 });
    }
  });

  console.log('📊 Validation errors:', newErrors);
  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  const handleSubmit = (e) => {
  e.preventDefault();
  
  console.log('🔍 handleSubmit called!');
  console.log('📦 Current config:', JSON.stringify(config, null, 2));
  
  if (validateForm()) {
    console.log('✅ Validation passed!');
    console.log('📤 MultiZoneConfig submitting:', config);
    onSave(config);
  } else {
    console.log('❌ Validation failed!', errors);
  }
};

  // Get content options basierend auf contentType
  const getContentOptions = (contentType) => {
    switch (contentType) {
      case 'playlist':
        return playlists;
      case 'widget':
        return widgets;
      case 'media':
        return media;
      default:
        return [];
    }
  };

  // Get content name by ID
  const getContentName = (contentType, contentId) => {
    const options = getContentOptions(contentType);
    const item = options.find(opt => opt.id?.toString() === contentId?.toString());
    return item?.name || item?.title || item?.filename || t('widgets.multiZone.noContentSelected');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Layout size={28} />
          <div>
            <h2 className="text-2xl font-bold">
              {existingConfig ? t('widgets.multiZone.editLayout') : t('widgets.multiZone.configTitle')}
            </h2>
            <p className="text-purple-100 text-sm mt-1">{t('widgets.multiZone.configSubtitle')}</p>
          </div>
        </div>
        <button
          onClick={onCancel}
          className="p-2 hover:bg-purple-700 rounded-lg transition-colors"
        >
          <X size={24} />
        </button>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Layout Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.multiZone.layoutName')} *
          </label>
          <input
            type="text"
            value={config.name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder={t('widgets.multiZone.layoutNamePlaceholder')}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
              errors.name ? 'border-red-500' : ''
            }`}
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            {t('widgets.multiZone.layoutNameHelp')}
          </p>
        </div>

        {/* Layout Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('widgets.multiZone.layoutType')} *
          </label>
          <select
            value={config.config.layoutType}
            onChange={(e) => handleLayoutTypeChange(e.target.value)}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          >
            {Object.keys(LAYOUT_TEMPLATES).map(layout => (
              <option key={layout} value={layout}>
                {t(`widgets.multiZone.layouts.${layout}`)}
              </option>
            ))}
          </select>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            {t('widgets.multiZone.layoutTypeHelp')}
          </p>
        </div>

        {/* Visual Layout Preview */}
        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Grid size={18} />
            {t('widgets.multiZone.previewLayout')}
          </h3>
          <div 
            className={`grid ${LAYOUT_TEMPLATES[config.config.layoutType].grid} gap-${config.config.gapSize}`}
            style={{ 
              backgroundColor: config.config.backgroundColor,
              padding: `${config.config.gapSize}px`,
              minHeight: '200px'
            }}
          >
            {config.config.zones.map((zone, idx) => (
              <div
                key={zone.zoneId}
                className="bg-gray-100 dark:bg-gray-700 rounded border-2 border-purple-300 dark:border-purple-600 flex items-center justify-center"
              >
                <span className="text-gray-600 dark:text-gray-300 font-medium">
                  {t('widgets.multiZone.zoneNumber', { number: idx + 1 })}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Zone Configuration */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Grid size={20} />
            {t('widgets.multiZone.zones')}
          </h3>

          {config.config.zones.map((zone, idx) => (
            <div
              key={zone.zoneId}
              className="border border-gray-300 dark:border-gray-600 rounded-lg p-4 space-y-4"
            >
              <h4 className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                  {idx + 1}
                </div>
                {t('widgets.multiZone.zoneConfiguration', { number: idx + 1 })}
              </h4>

              {/* Content Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('widgets.multiZone.contentType')} *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['playlist', 'widget', 'media'].map(type => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => handleZoneChange(idx, 'contentType', type)}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                        zone.contentType === type
                          ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
                          : 'border-gray-300 dark:border-gray-600 hover:border-purple-400'
                      }`}
                    >
                      {type === 'playlist' && <List size={18} />}
                      {type === 'widget' && <Zap size={18} />}
                      {type === 'media' && <Image size={18} />}
                      <span className="font-medium">
                        {t(`widgets.multiZone.contentTypes.${type}`)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Content Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('widgets.multiZone.selectContent')} *
                </label>
                <select
                  value={zone.contentId}
                  onChange={(e) => handleZoneChange(idx, 'contentId', e.target.value)}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${
                    errors[`zone-${idx}`] ? 'border-red-500' : ''
                  }`}
                >
                  <option value="">
                    {zone.contentType === 'playlist' && t('widgets.multiZone.selectPlaylist')}
                    {zone.contentType === 'widget' && t('widgets.multiZone.selectWidget')}
                    {zone.contentType === 'media' && t('widgets.multiZone.selectMedia')}
                  </option>
                  {getContentOptions(zone.contentType).map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name || item.title || item.filename}
                    </option>
                  ))}
                </select>
                {errors[`zone-${idx}`] && (
                  <p className="text-red-500 text-sm mt-1">{errors[`zone-${idx}`]}</p>
                )}
              </div>

              {/* Duration */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('widgets.multiZone.duration')}
                </label>
                <input
                  type="number"
                  min="5"
                  max="3600"
                  value={zone.duration}
                  onChange={(e) => handleZoneChange(idx, 'duration', parseInt(e.target.value))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  {t('widgets.multiZone.durationHelp')}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Appearance Settings */}
        <div className="border-t pt-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t('widgets.appearance')}
          </h3>

          {/* Background Color */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('widgets.multiZone.backgroundColor')}
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={config.config.backgroundColor}
                onChange={(e) => handleBackgroundColorChange(e.target.value)}
                className="h-10 w-20 border rounded cursor-pointer"
              />
              <input
                type="text"
                value={config.config.backgroundColor}
                onChange={(e) => handleBackgroundColorChange(e.target.value)}
                placeholder="#000000"
                className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {t('widgets.multiZone.backgroundColorHelp')}
            </p>
          </div>

          {/* Gap Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('widgets.multiZone.gapSize')}: {config.config.gapSize}px
            </label>
            <input
              type="range"
              min="0"
              max="32"
              value={config.config.gapSize}
              onChange={(e) => handleGapSizeChange(e.target.value)}
              className="w-full"
            />
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {t('widgets.multiZone.gapSizeHelp')}
            </p>
          </div>
        </div>

        {/* Footer Actions - MOVED INSIDE FORM */}
        <div className="border-t p-4 bg-gray-50 dark:bg-gray-900 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            <Save size={18} />
            {existingConfig ? t('widgets.multiZone.updateLayout') : t('widgets.multiZone.saveLayout')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MultiZoneConfig;